import { Handle, Position, type Node, type NodeProps } from '@xyflow/react';
import type { ProcessExecuteIntervalType, ProcessIntervalAnchor, ProcessTemplateTaskType } from '@econtractor/types';
import { getTaskStatusBadgeTone } from '../../shared/TaskStatusBadge';
import ProcessRunTaskStatus from '../OffProcessDir/ProcessTasksTable/ProcessRunTaskStatus';
import ProcessIntervalLabel from './ProcessIntervalLabel';
import {
    PROCESS_DIAGRAM_DEFAULT_SOURCE_HANDLE,
    PROCESS_DIAGRAM_TARGET_HANDLE,
    answerSourceHandleId,
} from './helpers/handles';

export type ProcessDiagramOutgoingItem = {
    templateId: number;
    dtId: number;
    name: string;
    answerKey: number | null;
    interval: ProcessExecuteIntervalType | null;
    intervalAnchor?: ProcessIntervalAnchor;
};

export type ProcessDiagramNodeData = {
    task: ProcessTemplateTaskType & {
        instanceStatus?: number | null;
        instancePending?: boolean;
        dateEnd?: number | null;
    };
    readOnly?: boolean;
    pruned?: boolean;
    current?: boolean;
    outgoing: ProcessDiagramOutgoingItem[];
    onIntervalClick: (templateId: number, dtId: number, answerKey: number | null) => void;
};

export type ProcessDiagramNodeType = Node<ProcessDiagramNodeData, 'processTask'>;

const handleStyle = { width: 10, height: 10, background: 'var(--bs-primary)' };

const diagramNodeFrameClass = (
    task: ProcessDiagramNodeData['task'],
    pruned?: boolean,
): string => {
    if (pruned) return 'process-diagram-node--pruned-frame';
    if (task.instancePending) return 'process-diagram-node--pending';
    if (task.instanceStatus == null || !Number.isFinite(Number(task.instanceStatus))) {
        return 'process-diagram-node--plain';
    }
    const tone = getTaskStatusBadgeTone(Number(task.instanceStatus), task.dateEnd);
    return `process-diagram-node--status-${tone}`;
};

export const ProcessDiagramNode = ({ data }: NodeProps<ProcessDiagramNodeType>) => {
    const { task, outgoing, readOnly, pruned, onIntervalClick } = data;
    const answerKeys = new Set((task.answers || []).map((answer) => answer.key));
    const defaultOutgoing = outgoing.filter((item) => item.answerKey == null || !answerKeys.has(item.answerKey));
    const showStatus =
        task.instancePending === true ||
        (task.instanceStatus != null && Number.isFinite(Number(task.instanceStatus)));
    const frameClass = diagramNodeFrameClass(task, pruned);

    return (
        <div
            className={`process-diagram-node rounded bg-body p-2 shadow-sm ${frameClass}${pruned ? ' process-diagram-node--pruned' : ''}`}
        >
            <Handle type="target" position={Position.Left} id={PROCESS_DIAGRAM_TARGET_HANDLE} style={handleStyle} />
            <Handle
                type="source"
                position={Position.Right}
                id={PROCESS_DIAGRAM_DEFAULT_SOURCE_HANDLE}
                style={{ ...handleStyle, top: 18 }}
            />
            <div className="process-diagram-node-drag fw-medium text-break">{task.taskName}</div>
            {showStatus || pruned ? (
                <div className="mt-1">
                    <ProcessRunTaskStatus task={task} pruned={pruned} />
                </div>
            ) : null}
            {defaultOutgoing.map((item) => (
                <button
                    key={`${item.templateId}:${item.dtId}:none`}
                    type="button"
                    className="btn btn-link btn-sm p-0 text-muted d-block text-start"
                    disabled={readOnly}
                    onClick={() => onIntervalClick(item.templateId, item.dtId, item.answerKey)}
                >
                    {item.name}: <ProcessIntervalLabel interval={item.interval} anchor={item.intervalAnchor} />
                </button>
            ))}
            {(task.answers || []).map((answer) => {
                const links = outgoing.filter((item) => item.answerKey === answer.key);
                return (
                    <div key={answer.key} className="process-diagram-answer-row d-flex align-items-center gap-2 mt-1">
                        <span className="badge text-bg-light text-truncate flex-grow-1 text-start" title={answer.text}>
                            {answer.text}
                        </span>
                        {links.map((item) => (
                            <button
                                key={`${item.templateId}:${item.dtId}:${item.answerKey}`}
                                type="button"
                                className="btn btn-link btn-sm p-0 text-muted"
                                disabled={readOnly}
                                onClick={() => onIntervalClick(item.templateId, item.dtId, item.answerKey)}
                            >
                                <ProcessIntervalLabel interval={item.interval} anchor={item.intervalAnchor} />
                            </button>
                        ))}
                        <Handle
                            type="source"
                            position={Position.Right}
                            id={answerSourceHandleId(answer.key)}
                            style={handleStyle}
                        />
                    </div>
                );
            })}
        </div>
    );
};
